import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import i18n from 'config/i18n'
import { STEP_TYPE_ENUM } from 'services/api/constants'
import { EngagementId } from 'state/tasks'

const engagementFieldsOrder: Record<STEP_TYPE_ENUM | string, EngagementId[]> = {
    // IG STORY
    [STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT]: [
        EngagementId.VIEW,
        EngagementId.LIKE,
        EngagementId.STICKER_TAP,
        EngagementId.REACH,
        EngagementId.IMPRESSION,
        EngagementId.LINK_CLICK,
    ],
    // IG POST
    [STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_POST]: [
        EngagementId.LIKE,
        EngagementId.COMMENT,
        EngagementId.VIEW,
        EngagementId.SHARE,
        EngagementId.TAG,
        EngagementId.REACH,
        EngagementId.IMPRESSION,
        EngagementId.SAVE,
    ],
    // IG REEL
    [STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_INSTAGRAM_REEL]: [
        EngagementId.LIKE,
        EngagementId.COMMENT,
        EngagementId.VIEW,
        EngagementId.SHARE,
        EngagementId.REACH,
        EngagementId.IMPRESSION,
        EngagementId.TOTAL_WATCH_TIME,
        EngagementId.AVERAGE_WATCH_TIME,
    ],
    // YOUTUBE VIDEO
    [STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_YOUTUBE_VIDEO]: [
        EngagementId.LIKE,
        EngagementId.COMMENT,
        EngagementId.VIEW,
    ],
    // OTHERS
    [STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_OTHERS]: [
        EngagementId.LIKE,
        EngagementId.COMMENT,
        EngagementId.SHARE,
        EngagementId.VIEW,
        EngagementId.REACH,
        EngagementId.IMPRESSION,
    ],
    // FACEBOOK POST
    [STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_FACEBOOK_POST]: [
        EngagementId.LIKE,
        EngagementId.COMMENT,
        EngagementId.SHARE,
        EngagementId.TAG,
        EngagementId.VIEW,
    ],
    // TIKTOK POST
    [STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TIKTOK_POST]: [
        EngagementId.LIKE,
        EngagementId.SHARE,
        EngagementId.SAVE,
        EngagementId.COMMENT,
        EngagementId.TAG,
        EngagementId.CTA_CLICK,
        EngagementId.REACH,
        EngagementId.IMPRESSION,
        EngagementId.TOTAL_WATCH_TIME,
        EngagementId.AVERAGE_WATCH_TIME,
        EngagementId.WATCHED_FULL_VIDEO,
    ],
    // TWITTER POST
    [STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TWITTER_POST]: [
        EngagementId.LIKE,
        EngagementId.REPLY,
        EngagementId.RETWEET,
        EngagementId.ENGAGEMENT,
        EngagementId.IMPRESSION,
    ],
    // XIAOHONGSHU POST
    [STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_XIAOHONGSHU_POST]: [
        EngagementId.LIKE,
        EngagementId.COMMENT,
        EngagementId.VIEW,
        EngagementId.SAVE,
        EngagementId.SHARE,
    ],
}

export const sortEngagementType = (stepType: STEP_TYPE_ENUM, engagementTypes: EngagementId[]) => {
    const orders = engagementFieldsOrder[stepType]
    if (!orders) {
        return engagementTypes
    }

    engagementTypes.sort((a: EngagementId, b: EngagementId): number => {
        const indexA = orders.indexOf(a)
        const indexB = orders.indexOf(b)
        if (indexA === -1 && indexB === -1) return 0
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
    })
    return engagementTypes
}

export const buildEngagementFieldColDef = (engagementType: EngagementId): GridColDef | null => {
    if (engagementType === EngagementId.LIKE) {
        return {
            field: 'engagement_like_count',
            headerName: i18n.t('submission.table.likesLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.COMMENT) {
        return {
            field: 'engagement_comment_count',
            headerName: i18n.t('submission.table.commentsLabel'),
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.VIEW) {
        return {
            field: 'engagement_view_count',
            headerName: i18n.t('submission.table.viewsLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.SHARE) {
        return {
            field: 'engagement_share_count',
            headerName: i18n.t('submission.table.sharesLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.TAG) {
        return {
            field: 'engagement_tag_count',
            headerName: i18n.t('submission.table.tagsDateLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.REACH) {
        return {
            field: 'engagement_reach_count',
            headerName: i18n.t('submission.table.reachLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.IMPRESSION) {
        return {
            field: 'engagement_impression_count',
            headerName: i18n.t('submission.table.impressionLabel'),
            width: 125,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.STICKER_TAP) {
        return {
            field: 'engagement_sticker_tap_count',
            headerName: i18n.t('submission.table.stickerTapsLabel'),
            width: 125,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.SAVE) {
        return {
            field: 'engagement_save_count',
            headerName: i18n.t('submission.table.savesLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.CTA_CLICK) {
        return {
            field: 'engagement_cta_click_count',
            headerName: i18n.t('submission.table.ctaButtonClicksLabel'),
            width: 165,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.REPLY) {
        return {
            field: 'engagement_reply_count',
            headerName: i18n.t('submission.table.repliesLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.RETWEET) {
        return {
            field: 'engagement_retweet_count',
            headerName: i18n.t('submission.table.retweetsLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.ENGAGEMENT) {
        return {
            field: 'engagement_engagement_count',
            headerName: i18n.t('submission.table.engagementLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.LINK_CLICK) {
        return {
            field: 'engagement_link_click_count',
            headerName: i18n.t('submission.table.linkClicksLabel'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.TOTAL_WATCH_TIME) {
        return {
            field: 'engagement_total_watch_time_second_count',
            headerName: i18n.t('engagementMetric.totalWatchTime'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.TOTAL_PLAY_TIME) {
        return {
            field: 'engagement_total_play_time_second_count',
            headerName: i18n.t('submission.campaignReport.metric.totalPlayTime'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.WATCHED_FULL_VIDEO) {
        return {
            field: 'engagement_watched_full_video_percentage_count',
            headerName: i18n.t('engagementMetric.watchedFullVideo'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    } else if (engagementType === EngagementId.AVERAGE_WATCH_TIME) {
        return {
            field: 'engagement_average_watch_time_second_count',
            headerName: i18n.t('engagementMetric.averageWatchTime'),
            width: 105,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        }
    }
    return null
}
